// src/features/datev/components/DatevPayrollReports.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { Download, FileText, Search, RefreshCw, AlertCircle, ChevronDown, ChevronRight, Users } from 'lucide-react';
import { datevPayrollReportsService, DOCUMENT_TYPES, DatevDocumentType, DatevDocumentsMetadata } from '../../../services/datev/DatevPayrollReportsService';
import { netlifyFetch } from '../../../shared/services/netlifyFetch';

interface DatevPayrollReportsProps {
  customerId: string;
  beraternummer: string;
  mandantennummer: string;
}

export const DatevPayrollReports: React.FC<DatevPayrollReportsProps> = ({
  customerId,
  beraternummer,
  mandantennummer
}) => {
  const clientId = `${beraternummer}-${mandantennummer}`;

  // State
  const [documentTypes, setDocumentTypes] = useState<DatevDocumentType[]>([]);
  const [metadata, setMetadata] = useState<DatevDocumentsMetadata | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [selectedDocTypes, setSelectedDocTypes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [datevErrorCode, setDatevErrorCode] = useState<string | null>(null);
  const [helpUrl, setHelpUrl] = useState<string | null>(null);
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  const [docTypesLoaded, setDocTypesLoaded] = useState(false);
  const [noDocumentsForPeriod, setNoDocumentsForPeriod] = useState(false);
  const [availableClients, setAvailableClients] = useState<{ consultant: string; ranges: string[]; count: number }[] | null>(null);

  // Default-Period: 2020-09-01 (Sandbox-Testdaten für Bestand 455148-1)
  useEffect(() => {
    setSelectedPeriod('2020-09-01');
  }, []);

  // Dokumententypen laden (welche sind freigeschaltet?)
  const loadDocumentTypes = useCallback(async () => {
    try {
      setError(null);
      setDatevErrorCode(null);
      setHelpUrl(null);
      const types = await datevPayrollReportsService.getDocumentTypes(customerId, clientId);
      setDocumentTypes(types);
      // Automatisch alle freigegebenen Dokumententypen auswählen
      const granted = types
        .filter(t => t.access === 'access_granted')
        .map(t => t.document_type);
      setSelectedDocTypes(granted);
      setDocTypesLoaded(true);
    } catch (err: any) {
      setError(err.error || 'Fehler beim Laden der Dokumententypen');
      setDatevErrorCode(err.datev_error || null);
      setHelpUrl(err.help_url || null);

      // Audit-Auflage 2: Bei Berechtigungsfehler freigegebene Mandanten anzeigen
      try {
        const res = await netlifyFetch(
          `https://intranet.xtax.de/.netlify/functions/datev-payrollreports/clients?customerId=${customerId}`
        );
        if (res.ok) {
          const data = await res.json();
          if (data.groups && data.groups.length > 0) {
            setAvailableClients(data.groups);
          }
        }
      } catch (e) {
        // Mandanten-Liste optional — Fehler still ignorieren
      }
    }
  }, [customerId, clientId]);

  useEffect(() => {
    loadDocumentTypes();
  }, [loadDocumentTypes]);

  // Verfügbare Auswertungen laden
  const loadMetadata = async () => {
    if (!selectedPeriod || selectedDocTypes.length === 0) return;

    setIsLoading(true);
    setError(null);
    setDatevErrorCode(null);
    setHelpUrl(null);
    setMetadata(null);
    setNoDocumentsForPeriod(false);

    try {
      // Schritt 1: Existenzprüfung — gibt es überhaupt Dokumente für diesen Zeitraum?
      const exists = await datevPayrollReportsService.checkDocumentsExist(
        customerId,
        clientId,
        selectedPeriod
      );

      if (!exists) {
        setNoDocumentsForPeriod(true);
        setIsLoading(false);
        return;
      }

      // Schritt 2: Metadaten laden — ein Call mit allen gewählten Dokumententypen
      // Die DATEV API akzeptiert komma-separierte Dokumententypen
      const data = await datevPayrollReportsService.getDocumentsMetadata(
        customerId,
        clientId,
        selectedDocTypes,
        { period: selectedPeriod }
      );
      setMetadata(data);
    } catch (err: any) {
      setError(err.error || 'Fehler beim Laden der Auswertungen');
      setDatevErrorCode(err.datev_error || null);
      setHelpUrl(err.help_url || null);
    } finally {
      setIsLoading(false);
    }
  };

  // PDF herunterladen (einzelner Mitarbeiter oder Mandanten-Auswertung)
  const handleDownload = async (docType: string, period: string, employeeNumber?: number) => {
    const downloadKey = `${docType}-${employeeNumber || 'all'}`;
    setIsDownloading(downloadKey);
    setError(null);
    setDatevErrorCode(null);

    try {
      const { blob, filename } = await datevPayrollReportsService.downloadDocuments(
        customerId,
        clientId,
        period,
        [docType],
        { employeeNumber, asZip: false }
      );
      datevPayrollReportsService.triggerDownload(blob, filename);
    } catch (err: any) {
      setError(err.error || 'Fehler beim Herunterladen');
      setDatevErrorCode(err.datev_error || null);
      setHelpUrl(err.help_url || null);
    } finally {
      setIsDownloading(null);
    }
  };

  // Alle Dokumente eines Typs herunterladen (ZIP)
  const handleDownloadAll = async (docType: string, period: string) => {
    const downloadKey = `${docType}-zip`;
    setIsDownloading(downloadKey);
    setError(null);
    setDatevErrorCode(null);

    try {
      const { blob, filename } = await datevPayrollReportsService.downloadDocuments(
        customerId,
        clientId,
        period,
        [docType],
        { asZip: true }
      );
      datevPayrollReportsService.triggerDownload(blob, filename);
    } catch (err: any) {
      setError(err.error || 'Fehler beim Herunterladen');
      setDatevErrorCode(err.datev_error || null);
      setHelpUrl(err.help_url || null);
    } finally {
      setIsDownloading(null);
    }
  };

  // DocType-Toggle
  const toggleDocType = (type: string) => {
    setSelectedDocTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  // Expand/Collapse
  const toggleExpand = (type: string) => {
    setExpandedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  // Freigegebene Dokumententypen
  const grantedTypes = documentTypes.filter(dt => dt.access === 'access_granted');
  const deniedTypes = documentTypes.filter(dt => dt.access === 'access_denied');

  // Period-Optionen generieren (letzte 12 Monate)
  const periodOptions: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 1; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    const label = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(d);
    periodOptions.push({ value, label });
  }

  /**
   * Uebersetzt DATEV-Fehlercodes in freundliche, professionell formulierte
   * Meldungen fuer den Anwender. Liefert null fuer unbekannte Codes - dann
   * faellt die UI auf den Original-Text aus error zurueck.
   */
  const humanizeDatevError = (code: string | null, rawError: string | null): {
    title: string;
    detail: string | null;
  } | null => {
    if (!code) return null;
    switch (code) {
      case 'restapi.AccessDenied':
        return {
          title: 'Keine Berechtigung fuer diesen Mandanten',
          detail: `Fuer den Bestand ${clientId} ist der Zugriff auf den Lohnauswertungsdatenservice nicht freigegeben. ` +
                  `Die Liste der freigegebenen Mandanten finden Sie unten.`
        };
      case 'restapi.NotFound':
        return {
          title: 'Mandant nicht gefunden',
          detail: `Der Bestand ${clientId} ist im DATEV-Rechenzentrum nicht vorhanden.`
        };
      default:
        // Unbekannter Code: Original-Text anzeigen, aber als technisches Detail kennzeichnen
        return {
          title: 'Anfrage an DATEV fehlgeschlagen',
          detail: rawError && rawError !== code ? rawError : `Technischer Hinweis: ${code}`
        };
    }
  };

  // Sonderfall: requires_reconnect ist nicht im error-Code, sondern ein Zustand,
  // der typischerweise mit datev_error als HTTP-Status kommt. Wir behandeln ihn
  // ueber das error-Feld text-basiert (Backend liefert "DATEV-Verbindung ... abgelaufen").
  const isReconnectNeeded = error !== null && /verbindung.*abgelaufen|requires?[_ ]?reconnect/i.test(error);
  const humanizedError = error ? humanizeDatevError(datevErrorCode, error) : null;

  return (
    <div>
      {/* Überschrift */}
      <div style={{
        backgroundColor: '#EFF6FF',
        border: '1px solid #DBEAFE',
        borderRadius: '6px',
        padding: '12px',
        marginBottom: '20px',
        fontSize: '14px',
        color: '#1E40AF'
      }}>
        <strong>DATEV Lohnauswertungsdatenservice:</strong> Hier können Sie Lohnauswertungen
        für den Bestand {clientId} aus dem DATEV-Rechenzentrum abrufen.
      </div>

      {/* Fehlermeldung — humanisiert */}
      {error && (
        <div style={{
          backgroundColor: isReconnectNeeded ? '#FEF3C7' : '#FEE2E2',
          border: `1px solid ${isReconnectNeeded ? '#FDE68A' : '#FECACA'}`,
          borderRadius: '6px',
          padding: '14px',
          marginBottom: '16px',
          fontSize: '14px',
          color: isReconnectNeeded ? '#78350F' : '#991B1B',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px'
        }}>
          <AlertCircle size={20} style={{ flexShrink: 0, marginTop: '1px' }} />
          <div style={{ flex: 1 }}>
            {isReconnectNeeded ? (
              <>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                  DATEV-Verbindung abgelaufen
                </div>
                <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                  Bitte oeffnen Sie den Tab <strong>Verbindung</strong> und authentifizieren Sie sich erneut bei DATEV.
                </div>
              </>
            ) : humanizedError ? (
              <>
                <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                  {humanizedError.title}
                </div>
                {humanizedError.detail && (
                  <div style={{ fontSize: '13px', lineHeight: '1.5' }}>
                    {humanizedError.detail}
                  </div>
                )}
                {datevErrorCode && (
                  <div style={{ fontSize: '11px', marginTop: '6px', color: '#7F1D1D', opacity: 0.7 }}>
                    DATEV-Code: <code style={{ fontFamily: 'monospace' }}>{datevErrorCode}</code>
                  </div>
                )}
              </>
            ) : (
              <div>{error}</div>
            )}
            {helpUrl && (
              <div style={{ marginTop: '8px' }}>
                <a href={helpUrl} target="_blank" rel="noopener noreferrer"
                  style={{
                    color: isReconnectNeeded ? '#78350F' : '#1E40AF',
                    textDecoration: 'underline',
                    fontSize: '13px'
                  }}>
                  DATEV-Hilfe oeffnen
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Audit-Auflage 2: Freigegebene Mandanten bei Berechtigungsfehler */}
      {availableClients && availableClients.length > 0 && error && (
        <div style={{
          backgroundColor: '#FEF3C7',
          border: '1px solid #FDE68A',
          borderRadius: '6px',
          padding: '14px',
          marginBottom: '16px',
          fontSize: '13px',
          color: '#78350F'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '8px' }}>
            Für diesen Scope freigegebene Mandanten:
          </div>
          {availableClients.map((group, idx) => (
            <div key={idx} style={{ marginBottom: '6px' }}>
              <span style={{ fontWeight: '500' }}>Beraternummer {group.consultant}:</span>{' '}
              Mandanten {group.ranges.join(', ')}
              <span style={{ color: '#92400E', fontSize: '12px', marginLeft: '8px' }}>
                ({group.count} Mandanten)
              </span>
            </div>
          ))}
          <div style={{ marginTop: '10px', fontStyle: 'italic', color: '#92400E', fontSize: '12px' }}>
            Wenden Sie sich an Ihren Steuerberater, falls Mandanten fehlen.
          </div>
        </div>
      )}

      {/* Freigegebene Dokumententypen */}
      {docTypesLoaded && grantedTypes.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 8px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
            Freigegebene Auswertungen
          </h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {grantedTypes.map(dt => {
              const isSelected = selectedDocTypes.includes(dt.document_type);
              return (
                <button
                  key={dt.document_type}
                  onClick={() => toggleDocType(dt.document_type)}
                  style={{
                    padding: '4px 10px',
                    fontSize: '12px',
                    borderRadius: '4px',
                    border: isSelected ? '1px solid #3B82F6' : '1px solid #D1D5DB',
                    backgroundColor: isSelected ? '#EFF6FF' : 'white',
                    color: isSelected ? '#1D4ED8' : '#374151',
                    cursor: 'pointer',
                    fontWeight: isSelected ? '600' : '400'
                  }}
                >
                  {dt.document_type}
                  <span style={{ marginLeft: '4px', fontSize: '11px', color: '#6B7280' }}>
                    {DOCUMENT_TYPES[dt.document_type] || ''}
                  </span>
                </button>
              );
            })}
          </div>
          {deniedTypes.length > 0 && (
            <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '6px' }}>
              Nicht freigeschaltet: {deniedTypes.map(dt => dt.document_type).join(', ')}
            </p>
          )}
        </div>
      )}

      {/* Zeitraum-Auswahl + Abrufen-Button */}
      <div style={{
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-end',
        marginBottom: '20px',
        flexWrap: 'wrap'
      }}>
        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
            Abrechnungszeitraum
          </label>
          <select
            value={selectedPeriod}
            onChange={e => setSelectedPeriod(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              minWidth: '200px',
              backgroundColor: 'white'
            }}
          >
            {periodOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
            oder manuell (JJJJ-MM-TT)
          </label>
          <input
            type="text"
            placeholder="z.B. 2020-09-01"
            value={selectedPeriod}
            onChange={e => setSelectedPeriod(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              width: '150px',
              backgroundColor: 'white'
            }}
          />
        </div>

        <button
          onClick={loadMetadata}
          disabled={isLoading || selectedDocTypes.length === 0}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            backgroundColor: selectedDocTypes.length === 0 ? '#9CA3AF' : '#3B82F6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: isLoading || selectedDocTypes.length === 0 ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            opacity: isLoading ? 0.6 : 1
          }}
        >
          {isLoading ? (
            <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
          ) : (
            <Search size={16} />
          )}
          {isLoading ? 'Lade...' : 'Auswertungen abrufen'}
        </button>

        {selectedDocTypes.length === 0 && (
          <span style={{ fontSize: '13px', color: '#EF4444' }}>
            Bitte mindestens einen Dokumententyp auswählen
          </span>
        )}
      </div>

      {/* Keine Dokumente für diesen Zeitraum */}
      {noDocumentsForPeriod && (
        <div style={{
          padding: '24px',
          textAlign: 'center',
          color: '#92400E',
          fontSize: '14px',
          backgroundColor: '#FEF3C7',
          border: '1px solid #FDE68A',
          borderRadius: '6px'
        }}>
          Für den Zeitraum <strong>{selectedPeriod}</strong> sind keine Lohnauswertungen im DATEV-Rechenzentrum vorhanden.
        </div>
      )}

      {/* Ergebnisse */}
      {metadata && (
        <div>
          {/* Mitarbeiter-Auswertungen */}
          {metadata.employee_documents && metadata.employee_documents.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                Mitarbeiter-Auswertungen
              </h4>

              {metadata.employee_documents.map(docGroup => (
                <div key={docGroup.document_type} style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  marginBottom: '8px',
                  overflow: 'hidden'
                }}>
                  {/* Dokumententyp-Header */}
                  <div
                    onClick={() => toggleExpand(docGroup.document_type)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 14px',
                      backgroundColor: '#F9FAFB',
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {expandedTypes.has(docGroup.document_type) ? (
                        <ChevronDown size={16} color="#6B7280" />
                      ) : (
                        <ChevronRight size={16} color="#6B7280" />
                      )}
                      <FileText size={16} color="#3B82F6" />
                      <span style={{ fontWeight: '600', fontSize: '14px' }}>
                        {docGroup.document_type}
                      </span>
                      <span style={{ fontSize: '13px', color: '#6B7280' }}>
                        — {DOCUMENT_TYPES[docGroup.document_type] || ''}
                      </span>
                      <span style={{
                        backgroundColor: '#DBEAFE',
                        color: '#1E40AF',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {docGroup.employees ? docGroup.employees.length : 0} Mitarbeiter
                      </span>
                    </div>

                    <button
                      onClick={e => {
                        e.stopPropagation();
                        const period = docGroup.employees?.[0]?.document?.[0]?.period || selectedPeriod;
                        handleDownloadAll(docGroup.document_type, period);
                      }}
                      disabled={isDownloading === `${docGroup.document_type}-zip`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '4px 10px',
                        backgroundColor: '#10B981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        opacity: isDownloading === `${docGroup.document_type}-zip` ? 0.6 : 1
                      }}
                    >
                      <Download size={14} />
                      {isDownloading === `${docGroup.document_type}-zip` ? 'Lädt...' : 'Alle (ZIP)'}
                    </button>
                  </div>

                  {/* Mitarbeiter-Liste */}
                  {expandedTypes.has(docGroup.document_type) && docGroup.employees && (
                    <div style={{ borderTop: '1px solid #E5E7EB' }}>
                      {docGroup.employees.map(emp => (
                        <div
                          key={emp.employee_number}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 14px 8px 44px',
                            borderBottom: '1px solid #F3F4F6',
                            fontSize: '13px'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Users size={14} color="#9CA3AF" />
                            <span style={{ fontWeight: '500' }}>
                              Personalnummer {emp.employee_number}
                            </span>
                            {emp.document && emp.document.length > 0 && (
                              <span style={{ color: '#6B7280', fontSize: '12px' }}>
                                ({emp.document.length} {emp.document.length === 1 ? 'Dokument' : 'Dokumente'})
                              </span>
                            )}
                          </div>

                          <button
                            onClick={() => {
                              const period = emp.document?.[0]?.period || selectedPeriod;
                              handleDownload(docGroup.document_type, period, emp.employee_number);
                            }}
                            disabled={isDownloading === `${docGroup.document_type}-${emp.employee_number}`}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '3px 8px',
                              backgroundColor: '#3B82F6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              opacity: isDownloading === `${docGroup.document_type}-${emp.employee_number}` ? 0.6 : 1
                            }}
                          >
                            <Download size={12} />
                            {isDownloading === `${docGroup.document_type}-${emp.employee_number}` ? 'Lädt...' : 'PDF'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Mandanten-Auswertungen */}
          {metadata.client_documents && metadata.client_documents.length > 0 && (
            <div>
              <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                Mandanten-Auswertungen
              </h4>

              {metadata.client_documents.map(docGroup => (
                <div key={docGroup.document_type} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  marginBottom: '8px',
                  backgroundColor: '#F9FAFB'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FileText size={16} color="#8B5CF6" />
                    <span style={{ fontWeight: '600', fontSize: '14px' }}>
                      {docGroup.document_type}
                    </span>
                    <span style={{ fontSize: '13px', color: '#6B7280' }}>
                      — {DOCUMENT_TYPES[docGroup.document_type] || ''}
                    </span>
                  </div>

                  <button
                    onClick={() => {
                      const period = docGroup.document?.[0]?.period || selectedPeriod;
                      handleDownload(docGroup.document_type, period);
                    }}
                    disabled={isDownloading === `${docGroup.document_type}-all`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 10px',
                      backgroundColor: '#3B82F6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      opacity: isDownloading === `${docGroup.document_type}-all` ? 0.6 : 1
                    }}
                  >
                    <Download size={14} />
                    {isDownloading === `${docGroup.document_type}-all` ? 'Lädt...' : 'PDF'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Keine Daten */}
          {(!metadata.employee_documents || metadata.employee_documents.length === 0) &&
           (!metadata.client_documents || metadata.client_documents.length === 0) && (
            <div style={{
              padding: '24px',
              textAlign: 'center',
              color: '#6B7280',
              fontSize: '14px',
              backgroundColor: '#F9FAFB',
              borderRadius: '6px'
            }}>
              Keine Auswertungen für den gewählten Zeitraum und die ausgewählten Dokumententypen verfügbar.
            </div>
          )}
        </div>
      )}

      {/* CSS für Spin-Animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
